/**
* Utils.ts
* @author  Abhilash Panwar (abpanwar) Hector Hernandez (hectorh)
* @copyright Microsoft 2018
* File containing utility functions.
*/
import { CoreUtils, cookieAvailable, isWindowObjectAvailable } from '@ms/1ds-core-js';
var _canUseLocalStorage = undefined;
/**
* Type of storage to differentiate between local storage and session storage
*/
export var StorageType;
(function (StorageType) {
    StorageType[StorageType["LocalStorage"] = 0] = "LocalStorage";
    StorageType[StorageType["SessionStorage"] = 1] = "SessionStorage";
})(StorageType || (StorageType = {}));
/**
    * helper method to set userId and sessionId cookie
    */
export function _setCookie(logger, name, value, domain) {
    var domainAttrib = "";
    var secureAttrib = "";
    if (!isWindowObjectAvailable) {
        return;
    }
    if (domain) {
        domainAttrib = ";domain=" + domain;
    }
    if (window.document.location && window.document.location.protocol === "https:") {
        secureAttrib = ";secure";
    }
    if (cookieAvailable()) {
        window.document.cookie = name + "=" + value + domainAttrib + ";path=/" + secureAttrib;
    }
}
/**
* generate random id string
*/
export function newId() {
    var base64chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
    var result = "";
    // tslint:disable-next-line:insecure-random
    var random = Math.random() * 1073741824; //5 symbols in base64, almost maxint
    while (random > 0) {
        var char = base64chars.charAt(random % 64);
        result += char;
        random = Math.floor(random / 64);
    }
    return result;
}
/**
     *  Check if the browser supports local storage.
     *
     *  @returns {boolean} True if local storage is supported.
     */
export function canUseLocalStorage() {
    if (_canUseLocalStorage === undefined) {
        _canUseLocalStorage = !!_getVerifiedStorageObject(StorageType.LocalStorage);
    }
    return _canUseLocalStorage;
}
/**
* Gets the localStorage object if available
* @return {Storage} - Returns the storage object if available else returns null
*/
function _getLocalStorageObject() {
    if (canUseLocalStorage()) {
        return _getVerifiedStorageObject(StorageType.LocalStorage);
    }
    return null;
}
/**
     * Tests storage object (localStorage or sessionStorage) to verify that it is usable
     * More details here: https://mathiasbynens.be/notes/localstorage-pattern
     * @param storageType Type of storage
     * @return {Storage} Returns storage object verified that it is usable
     */
function _getVerifiedStorageObject(storageType) {
    var storage = null;
    var fail;
    var uid;
    try {
        if (typeof window === 'undefined') {
            return null;
        }
        uid = new Date;
        storage = storageType === StorageType.LocalStorage ? window.localStorage : window.sessionStorage;
        storage.setItem(uid, uid);
        fail = storage.getItem(uid) != uid;
        storage.removeItem(uid);
        if (fail) {
            storage = null;
        }
    }
    catch (exception) {
        storage = null;
    }
    return storage;
}
/**
     *  Set the contents of an object in the browser's local storage
     *
     *  @param {string} name - the name of the object to set in storage
     *  @param {string} data - the contents of the object to set in storage
     *  @returns {boolean} True if the storage object could be written.
     */
export function setStorage(logger, name, data) {
    var storage = _getLocalStorageObject();
    if (storage !== null) {
        try {
            storage.setItem(name, data);
            return true;
        }
        catch (e) {
            _canUseLocalStorage = false;
            logger.warnToConsole("Browser failed write to local storage. " + e);
        }
    }
    return false;
}
/**
     *  Get an object from the browser's local storage
     *
     *  @param {string} name - the name of the object to get from storage
     *  @returns {string} The contents of the storage object with the given name. Null if storage is not supported.
     */
export function getStorage(logger, name) {
    var storage = _getLocalStorageObject();
    if (storage !== null) {
        try {
            return storage.getItem(name);
        }
        catch (e) {
            _canUseLocalStorage = false;
            logger.warnToConsole("Browser failed read of local storage. " + e);
        }
    }
    return null;
}
/**
 * A utility class that helps getting time related parameters
 */
var DateTimeUtils = /** @class */ (function () {
    function DateTimeUtils() {
    }
    /**
     * Get the number of milliseconds since 1970/01/01 in local timezone
     */
    DateTimeUtils.Now = (typeof window === 'undefined') ? function () { return new Date().getTime(); } :
        (window.performance && window.performance.now && window.performance.timing) ?
            function () {
                return window.performance.now() + window.performance.timing.navigationStart;
            }
            :
                function () {
                    return new Date().getTime();
                };
    /**
     * Gets duration between two timestamps
     */
    DateTimeUtils.GetDuration = function (start, end) {
        var result = null;
        if (start !== 0 && end !== 0 && !CoreUtils.isNullOrUndefined(start) && !CoreUtils.isNullOrUndefined(end)) {
            result = end - start;
        }
        return result;
    };
    return DateTimeUtils;
}());
export { DateTimeUtils };
//# sourceMappingURL=Utils.js.map